import resolveManual from "../craft/dataResolvers/resolveManual";
import getCkHtml from "./getCkHtml";

/* ───────────────── helpers ───────────────────────────────────────── */
const escAttr = s =>
  String(s)
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const escText = s =>
  String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const IND = lvl => "  ".repeat(lvl);

function toXmlName(name, fallback = "node") {
  const raw = String(name || "").trim();
  const cleaned = raw.replace(/[^A-Za-z0-9._-]/g, "_");
  if (!/^[A-Za-z_]/.test(cleaned)) return fallback;
  return cleaned || fallback;
}

const ARTICLE_TAGS = new Set([
  "articleLongform",
  "articleWithSteps",
  "articleLongformComposableEntries",
  "articleStepByStepComposableEntries",
]);

const BLOCK_TAGS = new Set([
  "textBlock",
  "textPlusImageBlock",
  "largeImageBlock",
  "calloutBlock",
  "videoBlock",
  "columnComposableBlock",
  "fullWidthImageBlock",
  "richTextInlineImagesBlock",
]);

function isArticleNode(node) {
  return node && ARTICLE_TAGS.has(node.type);
}

function isBlockNode(node) {
  return node && BLOCK_TAGS.has(node.type);
}

/**
 * Unwrap composableEntries case where resolveEntry() returns:
 *   { type: "textBlock", content: [ { type: "textBlock", content: {...} } ] }
 * so we get the actual normalized block object.
 */
function unwrapMaybeWrappedBlock(node) {
  if (!node) return null;

  // Already a normalized block: { type, content: { ... } }
  if (isBlockNode(node) && node.content && !Array.isArray(node.content)) return node;

  // Wrapped block entry: { type: "textBlock", content: [ normalizedBlock ] }
  if (isBlockNode(node) && Array.isArray(node.content) && node.content[0]) {
    const inner = node.content[0];
    if (isBlockNode(inner) && inner.content && !Array.isArray(inner.content)) return inner;
  }

  return node;
}

/* Custom XML prolog & epilog ---------------------------------------- */
const XML_HEAD = `<?xml version="1.0" encoding="UTF-8"?>
<!DOCTYPE manual SYSTEM "digiguide.dtd">
`;

const XML_FOOT = "\n";

/* ───────────────── block renderer ────────────────────────────────── */
function renderResolvedBlock(blockNode, lvl = 0) {
  const node = unwrapMaybeWrappedBlock(blockNode);
  if (!node) return "";

  const tag = toXmlName(node.type, "block");
  const pad = IND(lvl);
  const padIn = IND(lvl + 1);

  const id = node.id != null ? String(node.id) : "";
  const fieldHandle = node.content?.fieldHandle || node.fieldHandle || "";

  const content = node.content || {};
  let inner = "";

  // paragraph/richText already resolved by resolveEntry/resolveContentBlock into rest.paragraph (HTML)
  if (content.paragraph) inner += `${padIn}${content.paragraph}\n`;

  // richTextInlineImagesBlock uses content.richText (rawHtml was normalized into paragraph in resolver)
  if (content.richText) inner += `${padIn}${getCkHtml(content.richText)}\n`;

  // calloutText is plain; resolver also sets content.callout = { text, type }
  if (content.callout?.text) inner += `${padIn}${escText(content.callout.text)}\n`;

  // youtube link
  if (content.youtubeVideo) {
    const u = escAttr(content.youtubeVideo);
    inner += `${padIn}<a href="${u}">${u}</a>\n`;
  }

  // image
  const url = content.image?.url;
  if (url) inner += `${padIn}<img src="${escAttr(url)}" alt="" />\n`;

  // nested columns: columnComposableBlock → content.columnBlockComposableEntries (already resolved blocks)
  if (Array.isArray(content.columnBlockComposableEntries)) {
    content.columnBlockComposableEntries.forEach(child => {
      inner += renderResolvedBlock(child, lvl + 1);
    });
  }

  return `${pad}<${tag} id="${escAttr(id)}" fieldHandle="${escAttr(fieldHandle)}">
${inner}${pad}</${tag}>
`;
}

/* ───────────────── article renderer ──────────────────────────────── */
function renderArticle(article, lvl = 0) {
  const pad = IND(lvl);
  const tag = toXmlName(article.type, "article");

  let xml = `${pad}<${tag} id="${escAttr(article.id)}" fieldHandle="userTitle">
${pad}  <h2>${escText(article.title) || ""}</h2>
`;

  // articleLongform: content is array of normalized blocks
  // articleLongformComposableEntries: content is array of "entries" (often wrapped blocks) — handle both
  if (Array.isArray(article.content)) {
    article.content.forEach(item => {
      if (isArticleNode(item)) {
        xml += renderArticle(item, lvl + 1);
      } else {
        xml += renderResolvedBlock(item, lvl + 1);
      }
    });
  }

  // articleWithSteps: article.steps is array of { id, uid, type, content:[blocks] }
  if (Array.isArray(article.steps) && article.type === "articleWithSteps") {
    article.steps.forEach((step, idx) => {
      const stepPad = IND(lvl + 1);
      xml += `${stepPad}<step index="${idx + 1}" id="${escAttr(step.id)}">\n`;
      if (Array.isArray(step.content)) {
        step.content.forEach(b => {
          xml += renderResolvedBlock(b, lvl + 2);
        });
      }
      xml += `${stepPad}</step>\n`;
    });
  }

  // articleStepByStepComposableEntries: steps is array of resolved article entries
  if (
    Array.isArray(article.steps) &&
    article.type === "articleStepByStepComposableEntries"
  ) {
    article.steps.forEach((stepArticle, idx) => {
      const stepPad = IND(lvl + 1);
      xml += `${stepPad}<step index="${idx + 1}">\n`;
      if (isArticleNode(stepArticle)) {
        xml += renderArticle(stepArticle, lvl + 2);
      } else {
        // fallback: in case data comes through differently
        xml += renderResolvedBlock(stepArticle, lvl + 2);
      }
      xml += `${stepPad}</step>\n`;
    });
  }

  xml += `${pad}</${tag}>\n`;
  return xml;
}

/* ───────────────── chapter renderer ─────────────────────────────── */
function renderChapter(ch, lvl = 0) {
  const pad = IND(lvl);

  let xml = `${pad}<chapter id="${escAttr(ch.id)}" fieldHandle="userTitle">
${pad}  <h1>${escText(ch.title) || ""}</h1>
`;

  if (Array.isArray(ch.topics)) {
    ch.topics.forEach(t => {
      xml += renderArticle(t, lvl + 1);
    });
  }

  xml += `${pad}</chapter>\n`;
  return xml;
}

/* ───────────────── main export fn ───────────────────────────────── */
export function exportToInDesignXML(rawManual, globalVariables = []) {
  /* 1️⃣ resolve variables so final text is present */
  const manual = resolveManual(rawManual, globalVariables || []);

  let xml = `${XML_HEAD}<manual id="${escAttr(manual.id)}" lang="${escAttr(
    manual.language || ""
  )}">
  <title>${escText(manual.userTitle) || ""}</title>
`;

  manual.chapters.forEach(ch => {
    xml += renderChapter(ch, 1);
  });

  xml += `</manual>${XML_FOOT}`;
  return xml;
}

export default exportToInDesignXML;
